import { byId } from 'utils/common';
import { notNull } from 'utils/type_guards';

import type { Dictionary } from '@reduxjs/toolkit';
import type { OptionGroupAPIData } from 'domains/option_groups';
import type { NodeAPIData, ProductTemplateAPIData } from 'types/catalog_types';
import type { OverrideNode } from 'types/node_types';
import type { CanvasOptionConfiguration } from './canvases.types';

function buildCanvasProductNodes(productsById: Dictionary<ProductTemplateAPIData>, id?: number) {
  if (!id) return [];

  const product = productsById[id];

  if (!product) return [];

  return [...product.nodes].sort((a, b) => a.sort_order - b.sort_order);
}

function buildCanvasOverrideNodes(
  optionGroupsById: Dictionary<OptionGroupAPIData>,
  optionConfig?: CanvasOptionConfiguration
) {
  return Object.entries(optionConfig || [])
    .map(([ogId, oId]) => optionGroupsById[ogId]?.options.find(byId(oId)))
    .flatMap((o) => o?.nodes.map(formatAsOverrideNode))
    .filter(notNull);
}

// Removes props from option node data that we don't want to overwrite on the product node data
function formatAsOverrideNode(node: NodeAPIData) {
  const updated: Record<string, string | number | boolean> = {};

  Object.entries(node).forEach((pair) => {
    const key = pair[0] as keyof NodeAPIData;
    const value = pair[1] as NodeAPIData[typeof key];

    const exclude =
      key === 'sort_order' || // sorting always lives on product node data
      key === 'id' || // Don't overwrite the product id
      value === null ||
      value === 'null' || // remove nulls
      (value === '' && key === 'node_image'); // remove empty strings if they aren't the node_image

    if (!exclude) {
      updated[key] = value;
    }
  });

  return { ...updated, uid: node.uid } as OverrideNode;
}

export default {
  buildCanvasProductNodes,
  buildCanvasOverrideNodes,
  formatAsOverrideNode,
};
